import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "../api";
import ProductImage from "./ProductImage";

/**
 * Related products cross-sell strip. Renders 5–10 relevant products at the
 * bottom of every ProductDetail page. Prioritises same-category products,
 * then fills the remainder with other in-catalog SKUs. Boosts internal
 * linking depth for SEO (5–10 inbound links per product page) and helps
 * customers discover the broader catalog without leaving the PDP.
 *
 * Props:
 *  - currentSlug: slug of the product being viewed (excluded from results)
 *  - currentCategory: current product's category (used for smart ranking)
 *  - max: how many cards to render (default 8)
 */
export default function RelatedProducts({ currentSlug, currentCategory, max = 8 }) {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data || [])).catch(() => setProducts([]));
  }, []);

  const related = useMemo(() => {
    if (!products.length) return [];
    const others = products.filter((p) => p.slug !== currentSlug);

    // Sibling detection — a "sibling" is a product covering the same
    // compound in a different delivery form (e.g. Selank vial ↔
    // Selank Nasal Spray). Boosts these to the top: they're the strongest
    // possible cross-sell AND the highest-value internal link for SEO
    // (Google + LLMs both treat semantically-related products as authority
    // signal).
    const stripForm = (s) =>
      (s || "")
        .toLowerCase()
        .replace(/-nasal-spray$/, "")
        .replace(/^promist-bundle$/, "");
    const currentRoot = stripForm(currentSlug);
    const siblings = others.filter((p) => {
      if (!currentRoot) return false;
      const otherRoot = stripForm(p.slug);
      if (!otherRoot) return false;
      // Same compound root, different slug (i.e. different delivery form).
      return otherRoot === currentRoot && p.slug !== currentSlug;
    });
    // ProSource Mist bundle counts as a sibling for any single nasal spray.
    const isNasal = (currentSlug || "").endsWith("-nasal-spray");
    const bundleAsSibling = isNasal
      ? others.filter((p) => p.slug === "promist-bundle")
      : [];

    const siblingSet = new Set([...siblings, ...bundleAsSibling].map((p) => p.slug));
    const nonSiblings = others.filter((p) => !siblingSet.has(p.slug));
    const sameCat = nonSiblings.filter((p) => p.category === currentCategory);
    const otherCat = nonSiblings.filter((p) => p.category !== currentCategory);

    return [...siblings, ...bundleAsSibling, ...sameCat, ...otherCat].slice(0, max);
  }, [products, currentSlug, currentCategory, max]);

  if (!related.length) return null;

  // Cosmetic products (Skincare category) get a different heading — no
  // "Related research compounds / Explore related peptides" language, and
  // the section is hidden entirely for Blue Renewal until there are more
  // Skincare SKUs to cross-sell.
  const isCosmetic = currentCategory === "Skincare";
  if (isCosmetic) {
    // Only surface same-category (Skincare) siblings for cosmetic products.
    // Cross-selling a research vial from a cosmetic PDP is off-brand and
    // hurts the cosmetic positioning.
    const cosmeticRelated = related.filter((p) => p.category === "Skincare");
    if (!cosmeticRelated.length) return null;
  }

  return (
    <section
      className="mt-20 border-t border-white/10 pt-16"
      data-testid="related-products-section"
    >
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="overline">{isCosmetic ? "MORE FROM PROSOURCE LABS" : "RELATED RESEARCH COMPOUNDS"}</div>
          <h2 className="font-heading text-3xl sm:text-4xl mt-3 font-light">
            {isCosmetic ? "Explore more skincare" : "Explore related peptides"}
          </h2>
        </div>
        <Link
          to="/shop"
          className="inline-flex items-center gap-2 text-xs font-mono-tech uppercase tracking-[0.2em] text-[#00C3FF] hover:text-white"
          data-testid="related-view-all-link"
        >
          View Full Catalog <ArrowRight size={12} />
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-white/5">
        {related.map((p) => {
          const firstVariant = p.variants?.[0];
          const fromPrice = Number(firstVariant?.price || 0).toFixed(2);
          return (
            <Link
              key={p.id || p.slug}
              to={`/product/${p.slug}`}
              className="product-card group flex flex-col bg-[#0C0C12]"
              data-testid={`related-product-card-${p.slug}`}
            >
              <div className="vial-bg aspect-square flex items-center justify-center overflow-hidden relative">
                <ProductImage
                  product={p}
                  className="w-[68%] h-[80%] transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-4 flex-1 flex flex-col gap-1.5 border-t border-white/5">
                <div className="overline" style={{ fontSize: 9 }}>
                  {p.category}
                </div>
                <div className="font-heading text-base leading-tight text-white group-hover:text-[#00C3FF] transition-colors">
                  {p.name}
                </div>
                <div className="flex-1" />
                <div className="flex items-baseline justify-between pt-1">
                  <div className="font-mono-tech text-[9px] tracking-[0.2em] text-[#5C657A] uppercase">
                    From
                  </div>
                  <div className="font-mono-tech text-sm text-[#00C3FF]">${fromPrice}</div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
